/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  FormControl,
  TextField,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Button,
  useTheme,
  useMediaQuery,
  InputAdornment,
  Typography,
} from "@mui/material";
import * as yup from "yup";
import { useFormik } from "formik";
import * as Sentry from "@sentry/react";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { RootState, useAppDispatch } from "@store/store";
import {
  ApiError,
  GarantiasVehicularInmobiliariaResponse,
  PayloadRequestGarantias,
  PayloadRequestGarantiasVehicularInmobiliaria,
  PropertyTypeStrutureCatalog,
} from "@interfaces/index";
import ControlledPlaceAutocomplete from "@components/ControlledPlaceAutocomplete";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useLazyGetPropertyTypeQuery } from "@api/catalogs";
import { useSelector } from "react-redux";
import {
  usePatchGuaranteesTypesMutation,
  usePostGuaranteesTypesMutation,
} from "@api/garantiasApi";
import { GuaranteeFormHandle } from "./guaranteeFormHandle";

interface PropertyProps {
  type_inmobiliaria: string;
  valor_estimado: string;
  ubicacion: string;
  latitud: string;
  longitud: string;
  id?: string;
}

interface FormGarantiaInmobiliariaProps {
  guaranteesData: GarantiasVehicularInmobiliariaResponse[];
  guaranteeTypeId?: string;
  hideSubmitButton?: boolean;
  guaranteeLabel?: string;
  multiFlow?: boolean;
  onStatusChange?: (status: { isValid: boolean; isSubmitting: boolean }) => void;
  loading?: boolean;
}

const FormGarantiaInmobiliariaPymio = forwardRef<GuaranteeFormHandle, FormGarantiaInmobiliariaProps>(
  (
    {
      guaranteesData,
      guaranteeTypeId,
      hideSubmitButton,
      guaranteeLabel,
      onStatusChange,
      loading = false,
    }: FormGarantiaInmobiliariaProps,
    ref,
  ) => {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useAppDispatch();
  const application = useSelector((state: RootState) => state.register.application);
  const [triggerListPropertyType] = useLazyGetPropertyTypeQuery();
  const [postGuarantees] = usePostGuaranteesTypesMutation();
  const [patchGuarantees] = usePatchGuaranteesTypesMutation();

  const emptyProperty: PropertyProps = {
    type_inmobiliaria: "",
    valor_estimado: "",
    ubicacion: "",
    latitud: "",
    longitud: "",
    id: undefined,
  };

  const [isLoading, setIsLoading] = useState(loading);
  const [listPropertys, setListPropertys] = useState<PropertyTypeStrutureCatalog[]>([]);

  const lastSubmitOkRef = useRef(false);

  type FormValues = {
    properties: PropertyProps[];
  };

  const formik = useFormik<FormValues>({
    initialValues: {
      properties: [{ ...emptyProperty }],
    },
    validateOnMount: true,
    validationSchema: yup.object().shape({
      properties: yup.array().of(
        yup.object().shape({
          type_inmobiliaria: yup.string().required("Campo requerido"),
          valor_estimado: yup.string().required("Campo requerido"),
          ubicacion: yup.string().required("Campo requerido"),
        }),
      ),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        lastSubmitOkRef.current = false;
        setIsLoading(true);
        setSubmitting(true);

        const guaranteesToPatch: Array<{ guarantee_id: string; data: PayloadRequestGarantiasVehicularInmobiliaria }> =
          [];
        const guaranteesToPost: PayloadRequestGarantiasVehicularInmobiliaria[] = [];

        values.properties.forEach((property) => {
          const cleanValue = Number(property.valor_estimado.replace(/,/g, "")) || 0;

          const guaranteeItem: PayloadRequestGarantiasVehicularInmobiliaria = {
            loan_application_id: application.id,
            ...(guaranteeTypeId ? { guarantee_type_id: guaranteeTypeId } : {}),
            property_type_id: property.type_inmobiliaria,
            estimated_value: cleanValue,
            latitude: Number(property.latitud),
            longitude: Number(property.longitud),
            address: property.ubicacion,
          };

          if (property?.id) {
            guaranteesToPatch.push({ guarantee_id: property.id, data: guaranteeItem });
          } else {
            guaranteesToPost.push(guaranteeItem);
          }
        });

        // Si existe id => PATCH (uno por garantía). Si no existe => POST (en lista)
        await Promise.all([
          ...guaranteesToPatch.map(({ guarantee_id, data }) =>
            patchGuarantees({ loan_application_id: application.id, guarantee_id, data }).unwrap(),
          ),
          guaranteesToPost.length > 0
            ? postGuarantees({
                loan_application_id: application.id,
                data: { guarantees_list: guaranteesToPost } as PayloadRequestGarantias,
              }).unwrap()
            : Promise.resolve(),
        ]);

        lastSubmitOkRef.current = true;
      } catch (error) {
        lastSubmitOkRef.current = false;
        const api_error = error as ApiError;

        Sentry.captureException(error, {
          extra: {
            component: "InfoGarantiaInmobiliaria",
            values: formik.values,
          },
        });

        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setSubmitting(false);
        setIsLoading(false);
      }
    },
  });

  useEffect(() => {
    onStatusChange?.({ isValid: formik.isValid, isSubmitting: formik.isSubmitting });
  }, [formik.isValid, formik.isSubmitting, onStatusChange]);

  useImperativeHandle(
    ref,
    () => ({
      submit: async () => {
        lastSubmitOkRef.current = false;

        const errors = await formik.validateForm();
        if (Object.keys(errors).length > 0) {
          formik.setTouched({
            properties: formik.values.properties.map(() => ({
              type_inmobiliaria: true,
              valor_estimado: true,
              ubicacion: true,
            })),
          });
          return false;
        }

        await formik.submitForm();
        return lastSubmitOkRef.current;
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formik.values.properties],
  );

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setIsLoading(true);
        const response_property_type = await triggerListPropertyType().unwrap();

        setListPropertys(response_property_type);
        const response_garantia_data = guaranteesData;

        if (response_garantia_data.length > 0) {
          const mappedProperties = response_garantia_data
            .filter((item) => item.property_type_id !== null && item.active)
            .map((item) => {
              const num = Number(item.estimated_value);
              const formatted = num.toLocaleString("en-US");

              return {
                type_inmobiliaria: item.property_type_id || "",
                valor_estimado: formatted || "0",
                latitud: String(item.latitude || ""),
                longitud: String(item.longitude || ""),
                ubicacion: item.address || "",
                id: item.id,
              };
            });

          if (mappedProperties.length > 0) {
            formik.setFieldValue("properties", mappedProperties);
          }
        }
      } catch (error) {
        Sentry.captureException(error, {
          extra: {
            component: "Garantias-form-inmobiliaria",
            values: formik.values,
          },
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, [guaranteesData]);

  const getFieldError = (fieldName: keyof PropertyProps, index: number): string | undefined => {
    const propertyErrors = formik.errors.properties as Array<Partial<Record<keyof PropertyProps, string>>> | undefined;
    if (propertyErrors && propertyErrors[index] && propertyErrors[index][fieldName]) {
      return propertyErrors[index][fieldName];
    }
    return undefined;
  };

  const handleChangeValue = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const rawValue = e.target.value.replace(/[^0-9]/g, "");
    const numericValue = Number(rawValue);
    const formattedValue = numericValue.toLocaleString("en-US");
    formik.setFieldValue(`properties[${index}].valor_estimado`, formattedValue);
  };

  return (
    <Box>
      <form onSubmit={formik.handleSubmit} noValidate>
        <Typography
          mt={isMobileOrTablet ? 2 : 3}
          variant={isMobileOrTablet ? "body2" : "body1"}
          color={theme?.palette?.primary?.dark}
          textAlign={"center"}
        >
          Detalle de tu garantía {guaranteeLabel}
        </Typography>

        {formik.values.properties.map((property: PropertyProps, index: number) => (
          <Box key={index} mt={index > 0 ? 4 : 0}>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel id={`type_inmobiliaria-label-${index}`} required>
                Tipo de inmueble
              </InputLabel>
              <Select
                labelId={`type_inmobiliaria-label-${index}`}
                id={`type_inmobiliaria-${index}`}
                name={`properties[${index}].type_inmobiliaria`}
                fullWidth
                required
                label={`Tipo de inmueble`}
                value={property.type_inmobiliaria}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                error={Boolean(
                  formik.touched.properties?.[index]?.type_inmobiliaria && getFieldError("type_inmobiliaria", index),
                )}
                disabled={formik.isSubmitting || isLoading}
              >
                <MenuItem value={""}>Selecciona</MenuItem>

                {listPropertys?.map((propertyType) => (
                  <MenuItem key={propertyType.id} value={propertyType.id}>
                    {propertyType.name}
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.properties?.[index]?.type_inmobiliaria && getFieldError("type_inmobiliaria", index) && (
                <FormHelperText>{getFieldError("type_inmobiliaria", index)}</FormHelperText>
              )}
            </FormControl>

            <FormControl fullWidth>
              <TextField
                id={`valor_estimado-${index}`}
                name={`properties[${index}].valor_estimado`}
                label="Valor Estimado"
                margin="normal"
                fullWidth
                autoComplete="off"
                onBlur={formik.handleBlur}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChangeValue(e, index)}
                inputProps={{ maxLength: 20 }}
                value={property.valor_estimado}
                disabled={formik.isSubmitting || isLoading}
                error={Boolean(
                  formik.touched.properties?.[index]?.valor_estimado && getFieldError("valor_estimado", index),
                )}
                helperText={
                  formik.touched.properties?.[index]?.valor_estimado ? getFieldError("valor_estimado", index) : ""
                }
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            </FormControl>

            <ControlledPlaceAutocomplete
              value={{
                address: property.ubicacion,
                lat: Number(property.latitud) || 0,
                lng: Number(property.longitud) || 0,
              }}
              onChange={(newValue) => {
                formik.setFieldValue(`properties[${index}].ubicacion`, newValue.address);
                formik.setFieldValue(`properties[${index}].latitud`, newValue.lat);
                formik.setFieldValue(`properties[${index}].longitud`, newValue.lng);
              }}
              onBlur={() => formik.setFieldTouched(`properties[${index}].ubicacion`, true)}
              error={Boolean(formik.touched.properties?.[index]?.ubicacion && getFieldError("ubicacion", index))}
              helperText={formik.touched.properties?.[index]?.ubicacion ? getFieldError("ubicacion", index) : ""}
              disabled={formik.isSubmitting || isLoading}
            />
          </Box>
        ))}

        {!hideSubmitButton && (
          <Box display="flex" justifyContent="center" mt={isMobileOrTablet ? 2 : 3} mb={2}>
            <Button
              variant="default"
              type="submit"
              color="primary"
              disabled={formik.isSubmitting || isLoading || !formik.isValid}
            >
              Continuar
            </Button>
          </Box>
        )}
      </form>
    </Box>
  );
  },
);

FormGarantiaInmobiliariaPymio.displayName = "FormGarantiaInmobiliariaPymio";

export default FormGarantiaInmobiliariaPymio;
